import React, { useState, useRef } from "react";
import { Button, Dialog, DialogContent, DialogTitle } from '@mui/material';

// react-image-crop
import ReactCrop, { centerCrop, makeAspectCrop, convertToPixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

//react-cropper
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';

export const CircleCropper = ({ open, onClose, onSave, aspectRatio = 1, minDimension = 100, file, selectedImage }) => {
  const [crop, setCrop] = useState({
    unit: '%',
    width: 100,
    aspect: aspectRatio,
  });
  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const [croppedImage, setCroppedImage] = useState(null);

  const resetCrop = () => {
    setCrop({ unit: '%', width: 100, aspect: aspectRatio }); // Reset crop
    onClose();
  };

  const onCropChange = (crop, percentCrop) => setCrop(percentCrop);

  const onImageLoad = (e) => {
    const { naturalWidth: width, naturalHeight: height } = e.currentTarget;
    // Set the initial crop to be 50% of the image width
    const cropWidthInPercent = 50; // 50% of the image width
    const newCrop = makeAspectCrop(
      { unit: '%', width: cropWidthInPercent, height: cropWidthInPercent }, // 50% width and height
      aspectRatio,
      width,  // Actual image width
      height  // Actual image height
    );
    const centeredCrop = centerCrop(newCrop, width, height); // Center the crop
    setCrop(centeredCrop); // Set the crop state
  };

  const handleCropSave = () => {
    if (!crop || !imgRef.current) {
      console.error("No crop or image reference available");
      return;
    }
  
    // Get the image reference
    const image = imgRef.current;
  
    // Convert the percentage crop into pixel values
    const pixelCrop = {
      x: (crop.x / 100) * image.naturalWidth,
      y: (crop.y / 100) * image.naturalHeight,
      width: (crop.width / 100) * image.naturalWidth,
      height: (crop.height / 100) * image.naturalHeight,
    };
  
    if (!pixelCrop.width || !pixelCrop.height) {
      console.error("Invalid pixel crop dimensions:", pixelCrop);
      return;
    }
  
    // Set canvas size to the cropped area's size
    const canvas = previewCanvasRef.current;
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
  
    const ctx = canvas.getContext("2d");
  
    // Draw the cropped area onto the canvas
    ctx.drawImage(
      image, // Source image
      pixelCrop.x, // Source X
      pixelCrop.y, // Source Y
      pixelCrop.width, // Source width
      pixelCrop.height, // Source height
      0, // Destination X
      0, // Destination Y
      pixelCrop.width, // Destination width
      pixelCrop.height // Destination height
    );
  
    // Save the cropped image as a Data URL or File
    const croppedImageDataUrl = canvas.toDataURL();
    const croppedFile = new File([dataURLtoBlob(croppedImageDataUrl)], file.name || "croppedImage.jpg", {
      type: file.type || "image/jpeg",
    });
  
    console.log("Cropped File Details:", croppedFile);
  
    onSave(croppedFile); // Call the save function
    resetCrop();
    // console.log("Crop:", crop); // The crop percentages
    // console.log("Pixel Crop:", pixelCrop); // The converted pixel crop
    // console.log("Image Dimensions:", imgRef.current.naturalWidth, imgRef.current.naturalHeight); // Actual image dimensions
  };   

  const handleCancel = () => {
    resetCrop();
  };

  const dataURLtoBlob = (dataURL) => {
    const [header, data] = dataURL.split(',');
    const mime = header.match(/:(.*?);/)[1];
    const binStr = atob(data);
    const len = binStr.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binStr.charCodeAt(i);
    }
    return new Blob([bytes], { type: mime });
  };

  return (
    <Dialog open={open} onClose={onClose}
      PaperProps={{
        sx: { minWidth: 500, maxWidth: 600, minHeight: 500 }, // Set a fixed size for the dialog
      }}>
      <DialogTitle>Crop Image</DialogTitle>
      <DialogContent className="hide-scrollbar">
        {file && (
          <div className="w-100 flex-center" style={{ minWidth: '200px', position: 'relative'}}>
            <ReactCrop
              crop={crop}
              onChange={onCropChange}
              circularCrop
              keepSelection={false}
              aspect={aspectRatio}
              minWidth={minDimension}
            >
              <img ref={imgRef} src={selectedImage} onLoad={onImageLoad} alt="Crop preview" style={{ maxWidth: '100%' }} />
            </ReactCrop>
            <canvas ref={previewCanvasRef} style={{ display: 'none' }} />
          </div>
        )}
      </DialogContent>
      <DialogContent sx={{scrollbarWidth:"none"}}>
        <div className="d-flex justify-content-end mt-3 gap-3">
          <Button onClick={handleCancel} className="hover-red-bg">
            CANCEL
          </Button>
          <Button variant="contained" sx={{ backgroundColor: '#0044C6' }} onClick={handleCropSave}>
            SAVE
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};


export const RectangleCropper = ({ open, onClose, onSave, file, selectedImage, aspectRatio = 16 / 5 }) => {
  const cropperRef = useRef(null);

  const handleCropSave = async () => {
    if (!cropperRef.current || !cropperRef.current.getCroppedCanvas) {
      console.error("Cropper not initialized or getCroppedCanvas function not available.");
      return;
    }

    const canvas = cropperRef.current.getCroppedCanvas({
      width: 1920, // Cover image width
      height: 600, // Cover image height
    });

    if (canvas) {
      canvas.toBlob((blob) => {
        const croppedFile = new File([blob], file.name || 'croppedImage.jpg', {
          type: file.type || 'image/jpeg',
        });
        onSave(croppedFile); // Pass the cropped image back to the parent component
        onClose();
      }, 'image/jpeg');
    } else {
      console.error("Canvas not available after cropping.");
    }
  };

  const handleCropperInitialized = (instance) => {
    console.log("Cropper Initialized!");
    cropperRef.current = instance;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Crop Image</DialogTitle>
      <DialogContent>
        {selectedImage && (
          <div style={{ width: '100%', height: '400px', position: 'relative' }}>
            <Cropper
              ref={cropperRef}
              src={selectedImage}
              style={{ height: '100%', width: '100%' }}
              aspectRatio={aspectRatio} // Aspect ratio for cover image (rectangular shape)
              guides={true}
              cropBoxMovable={true}
              cropBoxResizable={true}
              viewMode={1}
              responsive={true}
              background={false}
              autoCropArea={1}
              onInitialized={handleCropperInitialized}
            />
          </div>
        )}
      </DialogContent>
      <DialogContent>
        <div className="d-flex justify-content-end mt-3 gap-3">
          <Button onClick={onClose} className="hover-red-bg">Cancel</Button>
          <Button onClick={handleCropSave} variant="contained" sx={{ backgroundColor: '#0044C6' }}>Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};


export const FreehandCropper = ({ open, onClose, onSave, file, selectedImage, autoCrop=true }) => { 
  const cropperRef = useRef(null);

  const handleCropSave = async () => {
    if (!cropperRef.current || !cropperRef.current.getCroppedCanvas) {
      console.error("Cropper not initialized or getCroppedCanvas function not available.");
      return;
    }

    const canvas = cropperRef.current.getCroppedCanvas({
      width: 1920, // Default cropped width (can still adjust this)
      height: 600, // Default cropped height (optional)
    });

    if (canvas) {
      canvas.toBlob((blob) => {
        const croppedFile = new File([blob], file.name || 'croppedImage.jpg', {
          type: file.type || 'image/jpeg',
        });
        onSave(croppedFile);
        onClose();
      }, 'image/jpeg');
    } else {
      console.error("Canvas not available after cropping.");
    }
  };

  const handleCropperInitialized = (instance) => {
    console.log("Cropper Initialized!");
    cropperRef.current = instance;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Crop Image</DialogTitle>
      <DialogContent>
        {selectedImage && (
          <div style={{ width: '100%', height: '400px', position: 'relative' }}>
            <Cropper
              ref={cropperRef}
              src={selectedImage}
              style={{ height: '100%', width: '100%' }}
              guides={true}
              cropBoxMovable={true}
              cropBoxResizable={true}
              viewMode={1}
              responsive={true}
              background={false}
              autoCrop={autoCrop}
              autoCropArea={1}
              onInitialized={handleCropperInitialized}
            />
          </div>
        )}
      </DialogContent>
      <DialogContent>
        <div className="d-flex justify-content-end mt-3 gap-3">
          <Button onClick={onClose} className="hover-red-bg">Cancel</Button>
          <Button onClick={handleCropSave} variant="contained" sx={{ backgroundColor: '#0044C6' }}>Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
